import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Radio, Music, Video, MessageCircle, Heart, Crown, ArrowRight, Play } from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";

const HERO = "https://images.unsplash.com/photo-1721623777765-1381ba32859c?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

const FEATURES = [
  { icon: Music, title: "Release Your Sound", desc: "Upload tracks in MP3/WAV with cover art. Fans stream in high fidelity from the persistent player." },
  { icon: Video, title: "Drop Music Videos", desc: "Publish cinematic visuals with a built-in theater player and view counts." },
  { icon: Heart, title: "Build a Following", desc: "Follow artists, like and comment. A social feed made for the culture." },
  { icon: MessageCircle, title: "Direct Messaging", desc: "Connect with collaborators and fans through private DMs." },
];

export default function Landing() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => { if (!loading && user) navigate("/feed", { replace: true }); }, [user, loading, navigate]);

  return (
    <div className="min-h-screen bg-[#0A0A0E] text-white overflow-x-hidden">
      {/* Nav */}
      <header className="glass-header fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="flex items-center justify-center w-9 h-9 rounded-xl bg-emerald-500/15 border border-emerald-500/40 glow-emerald">
              <Radio className="w-5 h-5 text-emerald-400" />
            </span>
            <span className="font-display font-extrabold text-lg">NEXT<span className="text-emerald-400">UP</span></span>
          </div>
          <button data-testid="landing-signin-btn" onClick={() => navigate("/auth")}
            className="px-5 py-2 rounded-full border border-white/15 hover:border-emerald-500/60 text-sm font-semibold transition-colors">
            Sign in
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0E] via-[#0A0A0E]/70 to-[#0A0A0E]/40" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0E] to-transparent" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 pt-24 w-full">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-mono-nu text-xs uppercase tracking-widest">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> The network for rising artists
            </span>
            <h1 className="mt-6 font-display font-black tracking-tight text-4xl sm:text-6xl lg:text-7xl leading-[1.05]">
              Where the next<br /><span className="text-gradient">wave</span> gets heard.
            </h1>
            <p className="mt-6 text-lg text-zinc-300 leading-relaxed max-w-lg">
              NEXT UP is the members-only social platform for music artists. Release tracks & videos, grow a following, and connect — all in one dark, distraction-free home for your craft.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <button data-testid="landing-get-started" onClick={() => navigate("/auth")}
                className="group flex items-center gap-2 px-7 py-3.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors">
                Join NEXT UP <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <span className="flex items-center gap-2 text-amber-400 font-mono-nu text-sm">
                <Crown className="w-4 h-4" /> Artist Pass · $25/mo
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-24">
        <h2 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight max-w-xl">Everything an artist needs to <span className="text-gradient">blow up</span>.</h2>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          {FEATURES.map((f, i) => (
            <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
              className="nu-card p-7 flex items-start gap-4">
              <span className="w-12 h-12 rounded-xl bg-emerald-500/10 border border-emerald-500/25 flex items-center justify-center shrink-0">
                <f.icon className="w-6 h-6 text-emerald-400" />
              </span>
              <div>
                <h3 className="font-bold text-xl">{f.title}</h3>
                <p className="mt-1 text-zinc-400 leading-relaxed">{f.desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-28">
        <div className="beam-border rounded-3xl p-10 sm:p-16 text-center relative overflow-hidden noise">
          <Play className="w-14 h-14 text-emerald-400 mx-auto" />
          <h2 className="mt-6 font-display font-black text-3xl sm:text-5xl tracking-tight">Your audience is waiting.</h2>
          <p className="mt-4 text-zinc-400 max-w-md mx-auto">Claim your spot on the platform built for the next generation of music.</p>
          <button data-testid="landing-cta-bottom" onClick={() => navigate("/auth")}
            className="mt-8 px-8 py-4 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-bold transition-colors">
            Get Started Free
          </button>
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-zinc-600 text-sm font-mono-nu">
        NEXT UP © 2026 — made for artists
      </footer>
    </div>
  );
}
